import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Automation, AutomationScript } from "@/common/utils/types";

/**
 * Type definitions for the Chrome Extension API injected by the content script
 */
interface ChromeExtensionAPI {
    extensionId: string;
    version?: string;
    isAvailable: boolean;
    supportsPauseResume?: boolean;
    triggerAutomation: (nameOrAutomation: string | AutomationScript, data?: any) => Promise<any>;
    getSettings: () => Promise<any>;
    setSettings: (settings: any) => Promise<void>;
    getAutomations: () => Promise<Record<string, Automation>>;
    setAutomations: (automations: any) => Promise<void>;
    onNavigationRequest: (handler: (data: any) => void) => void;
    startAudioCapture: (config?: AudioCaptureConfig) => Promise<any>;
    stopAudioCapture: (includeData?: boolean) => Promise<any>;
    pauseAudioCapture: () => Promise<any>;
    resumeAudioCapture: () => Promise<any>;
    getAudioStatus: () => Promise<any>;
    closeAudioCapture: () => Promise<void>;
    openOptionsPage: () => Promise<void>;
}

export interface AudioCaptureConfig {
    mimeType?: string;
    audioBitsPerSecond?: number;
    // EN-1609: who the session is for; the extension hands them back if a later start replaces it while paused.
    visitId?: string | null;
    transcriptSectionId?: string | null;
}

declare global {
    interface Window {
        chromeExtension?: ChromeExtensionAPI;
    }
}

/**
 * Context for managing Chrome Extension API access throughout the application.
 */
export const ChromeExtensionContext = createContext<
    | {
          isAvailable: boolean;
          isInitializing: boolean;
          extensionId: string | null;
          version: string | null;
          isSidebarWindow: boolean;
          error: Error | null;
          // API methods
          triggerAutomation: (nameOrAutomation: string | AutomationScript, data?: any) => Promise<any>;
          getSettings: () => Promise<any>;
          setSettings: (settings: any) => Promise<void>;
          getAutomations: () => Promise<Record<string, Automation>>;
          setAutomations: (automations: any) => Promise<void>;
          onNavigationRequest: (handler: (data: any) => void) => void;
          startAudioCapture: (config?: AudioCaptureConfig) => Promise<any>;
          stopAudioCapture: (includeData?: boolean) => Promise<any>;
          pauseAudioCapture: () => Promise<any>;
          resumeAudioCapture: () => Promise<any>;
          closeAudioCapture: () => Promise<void>;
          openOptionsPage: () => Promise<void>;
          // Raw API access for advanced use cases
          api: ChromeExtensionAPI | null;
      }
    | undefined
>(undefined);

/**
 * The ChromeExtensionProvider component detects and provides access to the Chrome Extension API
 * that is injected by the content script. It uses a hybrid detection approach:
 * 1. Checks immediately if the API is available
 * 2. Listens for the 'chromeExtensionReady' event for late injection
 *
 * @param {React.ReactNode} children - The children components that will consume the context.
 */
export const ChromeExtensionProvider = ({ children }: { children: React.ReactNode }) => {
    const [isAvailable, setIsAvailable] = useState<boolean>(false);
    const [isInitializing, setIsInitializing] = useState<boolean>(true);
    const [extensionId, setExtensionId] = useState<string | null>(null);
    const [version, setVersion] = useState<string | null>(null);
    const [isSidebarWindow, setIsSidebarWindow] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [api, setApi] = useState<ChromeExtensionAPI | null>(null);
    const navigationHandlerRef = useRef<((data: any) => void) | null>(null);

    /**
     * Checks if the Chrome Extension API is available on the window object
     */
    const checkApiAvailability = useCallback((): boolean => {
        if (typeof window === "undefined") {
            return false;
        }

        const extensionApi = (window as any).chromeExtension;

        if (extensionApi && extensionApi.isAvailable) {
            // Default supportsPauseResume to false if not set by the content script.
            // The Chrome Extension API has no pause/resume capability; any content script
            // that does not include the field gets a safe default of false.
            if (!("supportsPauseResume" in extensionApi)) {
                extensionApi.supportsPauseResume = false;
            }
            setApi(extensionApi);
            setExtensionId(extensionApi.extensionId);
            setVersion(extensionApi.version || null);
            setIsSidebarWindow(Boolean((extensionApi as any).isSidebarWindow));
            setIsAvailable(true);
            setError(null);

            // Re-register navigation handler if it exists
            if (navigationHandlerRef.current) {
                extensionApi.onNavigationRequest(navigationHandlerRef.current);
            }

            return true;
        }

        return false;
    }, []);

    /**
     * Wraps API calls with error handling
     */
    const safeApiCall = useCallback(
        async <T,>(operation: string, apiCall: () => Promise<T>): Promise<T> => {
            if (!api || !isAvailable) {
                const err = new Error(`Chrome Extension API is not available. Cannot ${operation}.`);
                setError(err);
                throw err;
            }

            try {
                setError(null);
                return await apiCall();
            } catch (err) {
                const error = err instanceof Error ? err : new Error(`Failed to ${operation}: ${String(err)}`);
                setError(error);

                // Log the full error for debugging
                console.error(`[ChromeExtensionProvider] Error in ${operation}:`, error);

                // Log unexpected errors to Sentry
                if (!error.message.includes("timeout") && !error.message.includes("not available")) {
                    Sentry.captureException(error, {
                        tags: { operation, extensionId: extensionId || "unknown" },
                    });
                }

                throw error;
            }
        },
        [api, isAvailable, extensionId],
    );

    // API method wrappers
    const triggerAutomation = useCallback(
        async (nameOrAutomation: string | AutomationScript, data?: any): Promise<any> => {
            return safeApiCall("trigger automation", () =>
                (api as ChromeExtensionAPI).triggerAutomation(nameOrAutomation, data),
            );
        },
        [safeApiCall, api],
    );

    const getSettings = useCallback(async (): Promise<any> => {
        return safeApiCall("get settings", () => (api as ChromeExtensionAPI).getSettings());
    }, [safeApiCall, api]);

    const setSettings = useCallback(
        async (settings: any): Promise<void> => {
            return safeApiCall("set settings", () => (api as ChromeExtensionAPI).setSettings(settings));
        },
        [safeApiCall, api],
    );

    const getAutomations = useCallback(async (): Promise<any> => {
        return safeApiCall("get automations", () => (api as ChromeExtensionAPI).getAutomations());
    }, [safeApiCall, api]);

    const setAutomations = useCallback(
        async (automations: any): Promise<void> => {
            return safeApiCall("set automations", () => (api as ChromeExtensionAPI).setAutomations(automations));
        },
        [safeApiCall, api],
    );

    const onNavigationRequest = useCallback(
        (handler: (data: any) => void): void => {
            // Always store the handler so it can be registered when API becomes available
            navigationHandlerRef.current = handler;

            if (!api || !isAvailable) {
                // Handler is stored and will be registered when checkApiAvailability detects the API
                return;
            }

            api.onNavigationRequest(handler);
        },
        [api, isAvailable],
    );

    const startAudioCapture = useCallback(
        async (config?: AudioCaptureConfig): Promise<any> => {
            return safeApiCall("start audio capture", () => (api as ChromeExtensionAPI).startAudioCapture(config));
        },
        [safeApiCall, api],
    );

    const stopAudioCapture = useCallback(
        async (includeData?: boolean): Promise<any> => {
            return safeApiCall("stop audio capture", () => (api as ChromeExtensionAPI).stopAudioCapture(includeData));
        },
        [safeApiCall, api],
    );

    const pauseAudioCapture = useCallback(async (): Promise<any> => {
        return safeApiCall("pause audio capture", () => (api as ChromeExtensionAPI).pauseAudioCapture());
    }, [safeApiCall, api]);

    const resumeAudioCapture = useCallback(async (): Promise<any> => {
        return safeApiCall("resume audio capture", () => (api as ChromeExtensionAPI).resumeAudioCapture());
    }, [safeApiCall, api]);

    const closeAudioCapture = useCallback(async (): Promise<void> => {
        return safeApiCall("close audio capture", () => (api as ChromeExtensionAPI).closeAudioCapture());
    }, [safeApiCall, api]);

    const openOptionsPage = useCallback(async (): Promise<void> => {
        return safeApiCall("open options page", () => (api as ChromeExtensionAPI).openOptionsPage());
    }, [safeApiCall, api]);

    // Effect: Initial check and event listener setup
    useEffect(() => {
        // Immediate check
        if (checkApiAvailability()) {
            setIsInitializing(false);
            return;
        }

        // Listen for the chromeExtensionReady event (for late injection)
        const handleExtensionReady = () => {
            if (checkApiAvailability()) {
                setIsInitializing(false);
            }
        };

        // Set a timeout to stop initializing if API doesn't become available
        const timeout = setTimeout(() => {
            if (!isAvailable) {
                setIsInitializing(false);
                setError(new Error("Chrome Extension API did not become available within expected time."));
            }
        }, 5000); // 5 second timeout

        document.addEventListener("chromeExtensionReady", handleExtensionReady);

        return () => {
            document.removeEventListener("chromeExtensionReady", handleExtensionReady);
            clearTimeout(timeout);
        };
    }, [checkApiAvailability, isAvailable]);

    // Effect: Bootstrap window.chromeExtension via postMessage for the native side panel iframe path
    useEffect(() => {
        if (typeof window === "undefined") return;

        let requestCounter = 0;
        let trustedParentOrigin: string | null = null;
        const pendingRequests = new Map<
            number,
            { resolve: (v: any) => void; reject: (e: Error) => void; timeout: ReturnType<typeof setTimeout> }
        >();

        const sendToParent = (operation: string, payload?: any): Promise<any> =>
            new Promise((resolve, reject) => {
                if (!trustedParentOrigin) {
                    reject(new Error("Parent origin is not initialized."));
                    return;
                }
                const id = ++requestCounter;
                const ms =
                    operation === "START_AUDIO_CAPTURE"
                        ? 30000
                        : operation === "PAUSE_AUDIO_CAPTURE" || operation === "RESUME_AUDIO_CAPTURE"
                          ? 8000
                          : 10000;
                const timeout = setTimeout(() => {
                    pendingRequests.delete(id);
                    reject(new Error(`Request timeout: ${operation}`));
                }, ms);
                pendingRequests.set(id, { resolve, reject, timeout });
                window.parent.postMessage(
                    { type: "WEBAPP_API_CALL", operation, payload: payload ?? {}, requestId: id },
                    trustedParentOrigin,
                );
            });

        const handleMessage = (event: MessageEvent) => {
            const msg = event.data;

            if (
                msg?.type === "EXTENSION_METADATA" &&
                msg.isSidePanel === true &&
                event.source === window.parent &&
                event.origin === `chrome-extension://${msg.extensionId}` &&
                (!window.chromeExtension || msg.isSidePanel === true)
            ) {
                trustedParentOrigin = event.origin;
                const api: ChromeExtensionAPI & { isSidebarWindow: boolean } = {
                    extensionId: msg.extensionId,
                    version: msg.extensionVersion,
                    isAvailable: true,
                    supportsPauseResume: true,
                    isSidebarWindow: true,
                    triggerAutomation: (nameOrAutomation, data) =>
                        typeof nameOrAutomation === "object" && nameOrAutomation !== null
                            ? sendToParent("TRIGGER_AUTOMATION", { automation: nameOrAutomation, data })
                            : sendToParent("TRIGGER_AUTOMATION", { name: nameOrAutomation, data }),
                    getSettings: () => sendToParent("GET_SETTINGS"),
                    setSettings: (settings) => sendToParent("SET_SETTINGS", { settings }),
                    getAutomations: () => sendToParent("GET_AUTOMATIONS"),
                    setAutomations: (automations) => sendToParent("SET_AUTOMATIONS", { automations }),
                    onNavigationRequest: () => {},
                    startAudioCapture: (config) => sendToParent("START_AUDIO_CAPTURE", { config }),
                    stopAudioCapture: (includeData) => sendToParent("STOP_AUDIO_CAPTURE", { includeData }),
                    pauseAudioCapture: () => sendToParent("PAUSE_AUDIO_CAPTURE"),
                    resumeAudioCapture: () => sendToParent("RESUME_AUDIO_CAPTURE"),
                    getAudioStatus: () => sendToParent("GET_AUDIO_STATUS"),
                    closeAudioCapture: () => sendToParent("CLOSE_AUDIO_CAPTURE"),
                    openOptionsPage: () => sendToParent("OPEN_OPTIONS_PAGE"),
                };
                window.chromeExtension = api;
                document.dispatchEvent(
                    new CustomEvent("chromeExtensionReady", {
                        detail: { extensionId: api.extensionId, isAvailable: true },
                    }),
                );
                // Directly update React state so the isSidebarWindow change is in the same
                // synchronous batch as the handshake completion.  Calling checkApiAvailability()
                // here would work for the other state fields but its two-level indirection
                // (closure → window read → setter) means setIsInitializing is never called in
                // this path, so the context update that reaches EditorProvider comes only from
                // the secondary first-effect re-run — which React may defer.  Calling the
                // setters directly guarantees all fields, including isSidebarWindow, land in one
                // committed render before any consumer (EditorProvider) can see a stale value.
                setApi(api);
                setExtensionId(api.extensionId);
                setVersion(api.version || null);
                setIsSidebarWindow(true);
                setIsAvailable(true);
                setIsInitializing(false);
                setError(null);
                if (navigationHandlerRef.current) {
                    api.onNavigationRequest(navigationHandlerRef.current);
                }
                return;
            }

            if (
                msg?.type === "WEBAPP_API_RESPONSE" &&
                event.source === window.parent &&
                trustedParentOrigin !== null &&
                event.origin === trustedParentOrigin
            ) {
                const pending = pendingRequests.get(msg.requestId);
                if (!pending) return;
                clearTimeout(pending.timeout);
                pendingRequests.delete(msg.requestId);
                if (msg.success) {
                    pending.resolve(msg.data);
                } else {
                    // EN-1609: keep the extension's response on the error; a failed start can still
                    // carry an orphaned paused session's audio that must be saved.
                    pending.reject(Object.assign(new Error(msg.error ?? "Unknown error"), { data: msg.data }));
                }
            }
        };

        window.addEventListener("message", handleMessage);

        // Guard: only post when inside an iframe — standalone tabs have no sidepanel parent.
        // ancestorOrigins[0] is the immediate parent frame's origin (chrome-extension://<id>).
        // Using it instead of '*' scopes the handshake message to the known parent rather than
        // broadcasting to any window that could be the parent.
        if (window.parent !== window) {
            const parentOrigin = window.location.ancestorOrigins?.[0] ?? "*";
            window.parent.postMessage({ type: "WEBAPP_READY" }, parentOrigin);
        }

        return () => {
            window.removeEventListener("message", handleMessage);
            pendingRequests.forEach(({ timeout }) => clearTimeout(timeout));
        };
    }, []);

    // Effect: Periodic check as fallback (in case event doesn't fire)
    useEffect(() => {
        if (isAvailable || !isInitializing) {
            return;
        }

        const interval = setInterval(() => {
            if (checkApiAvailability()) {
                setIsInitializing(false);
            }
        }, 1000); // Check every second while initializing

        return () => clearInterval(interval);
    }, [isAvailable, isInitializing, checkApiAvailability]);

    return (
        <ChromeExtensionContext.Provider
            value={{
                isAvailable,
                isInitializing,
                extensionId,
                version,
                isSidebarWindow,
                error,
                triggerAutomation,
                getSettings,
                setSettings,
                getAutomations,
                setAutomations,
                onNavigationRequest,
                startAudioCapture,
                stopAudioCapture,
                pauseAudioCapture,
                resumeAudioCapture,
                closeAudioCapture,
                openOptionsPage,
                api,
            }}
        >
            {children}
        </ChromeExtensionContext.Provider>
    );
};

/**
 * Custom hook to consume Chrome Extension context. It ensures the context is used within its provider.
 * Throws an error if used outside the `ChromeExtensionProvider`.
 *
 * @returns The Chrome Extension context with API methods and availability state.
 *
 * @example
 * ```tsx
 * const { isAvailable, triggerAutomation } = useChromeExtension();
 *
 * if (isAvailable) {
 *   await triggerAutomation('myAutomation', { data: 'value' });
 * }
 * ```
 */
export const useChromeExtension = () => {
    const context = useContext(ChromeExtensionContext);
    if (context === undefined) {
        throw new Error("useChromeExtension must be used within a ChromeExtensionProvider");
    }
    return context;
};
